import { SessionContext } from "@/context/SessionContext";
import ConductionAPI from "@/javascript/conduction-api";
import { Classroom } from "@/javascript/user-objects";
import { Utils } from "@/javascript/utils";
import { Button, Flex, Loader, Modal, Stack, Text, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";
import { useContext, useState } from "react";

interface ClassroomDuplcatorModalProps {
	classroom: Classroom | null;
	showModal: boolean;
	setShowModal: (show: boolean) => void;
	onDuplication?: () => void;
}

const ClassroomDuplcatorModal: React.FC<ClassroomDuplcatorModalProps> = ({
	classroom,
	showModal,
	setShowModal,
	onDuplication,
}) => {
	const [loading, setLoading] = useState<boolean>(false);

	const session = useContext(SessionContext);
	if (!session) {
		throw new Error("ClassroomDuplicatorModal must be a child of SessionContext.");
	}
	const token = session.token;
	if (token === null) {
		return null;
	}

	// Set up the form with initial values and validations.
	const form = useForm({
		initialValues: {
			name: ""
		},
		validate: {
			name: (value) => (value.trim().length === 0 ? "Classroom name is required" : null)
		},
	});

	const handleSubmit = async () => {
		if (classroom === null) return;

		// Validate the form and display errors if necessary.
		if (!form.isValid()) {
			form.validate();
			return;
		}

		setLoading(true);

		const notificationId = Utils.displayNotification(
			"Creating Classroom...",
			"Your new classroom is being created.",
			"blue",
			"loading"
		);

		const name = form.values.name;

		try {
			await ConductionAPI.duplicateClassroom(classroom.id, name, token);
			Utils.updateNotification(
				notificationId,
				`${name} Classroom Duplicated Successfully`,
				undefined,
				"green",
				<IconCheck size={16} />
			);

			if (onDuplication) {
				onDuplication();
			}
			setShowModal(false);
		} catch (error) {
			console.error("Error creating classroom", error);
			Utils.updateNotification(
				notificationId,
				"Error",
				"Failed to create classroom.",
				"red",
				<IconAlertCircle size={16} />,
				true
			);
		} finally {
			setLoading(false);
		}
	};

	const height = 29; // Use 40vh for view height as in the AdminSongCreator

	return (
		<Modal
			opened={showModal}
			onClose={() => setShowModal(false)}
			title="Duplicate Classroom"
			size="lg"
		>
			{loading ? (
				<Loader height={"100%"} radius="md" />
			) : (
				<Stack>
					<Text>
						Duplicate the {classroom?.name ?? "Error"} classroom?
					</Text>
					<form onSubmit={form.onSubmit(handleSubmit)}>
						<Flex
							direction="column"
							justify="space-between"
							align="stretch"
							style={{ minHeight: `${height}vh` }}
							onKeyDown={(e) => e.stopPropagation()} // Prevent hotkey conflicts
							onKeyUp={(e) => e.stopPropagation()}
						>
							<Stack>
								<TextInput
									label="New Classroom Name"
									{...form.getInputProps("name")}
									required
								/>
							</Stack>

							<Button
								type="submit"
								radius="xl"
								size="sm"
								loading={form.submitting}
							>
								Duplicate Classroom
							</Button>
						</Flex>
					</form>
				</Stack>
			)}
		</Modal>
	);
};

export default ClassroomDuplcatorModal;
